import React from "react";

import type { RowData } from "@tanstack/react-table-v9";

import { gridSpacing } from "@utils";

import TableV2IconButton from "./TableV2IconButton";
import Select from "../../Select/Select";
import { PAGE_SIZE_OPTIONS } from "../hooks/useTableV2";
import type { TableV2Instance } from "../hooks/useTableV2";
import { PageSizeSelect, Pagination, PaginationControls } from "../styles";

export interface TableV2PaginationProps<TData extends RowData> {
  table: TableV2Instance<TData>;
  isLoading?: boolean;
  pageSizeOptions?: number[];
}

const formatCount = (n: number) => n.toLocaleString("en-US");

const compactSelect = {
  hideSearch: true,
  controlStyle: { minHeight: 28, height: 28 },
  valueContainerStyle: { padding: `0 ${gridSpacing[2]}px` },
  indicatorsContainerStyle: { height: 26 },
};

const TableV2Pagination = <TData extends RowData>({
  table,
  isLoading = false,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: TableV2PaginationProps<TData>) => {
  const { pageIndex, pageSize } = table.state.pagination;
  const total = table.getRowCount();
  const first = total === 0 ? 0 : pageIndex * pageSize + 1;
  const last = Math.min((pageIndex + 1) * pageSize, total);

  return (
    <Pagination role="navigation">
      <span>
        <strong>{formatCount(total)}</strong> total
      </span>
      <PaginationControls>
        Rows per page
        <PageSizeSelect>
          <Select
            {...compactSelect}
            disabled={isLoading}
            value={String(pageSize)}
            options={pageSizeOptions.map((size) => {
              return { label: String(size), value: String(size) };
            })}
            onChange={(value) => table.setPageSize(Number(value))}
            style={{ paddingBottom: 0 }}
          />
        </PageSizeSelect>
        <span>
          {formatCount(first)}–{formatCount(last)} of {formatCount(total)}
        </span>
        <span>
          <TableV2IconButton
            tooltip={false}
            icon="fa-solid fa-chevron-left"
            label="Previous page"
            disabled={isLoading || !table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          />
          <TableV2IconButton
            tooltip={false}
            icon="fa-solid fa-chevron-right"
            label="Next page"
            disabled={isLoading || !table.getCanNextPage()}
            onClick={() => table.nextPage()}
          />
        </span>
      </PaginationControls>
    </Pagination>
  );
};

export default TableV2Pagination;
